import { get, isFinite } from 'lodash';

import {
  BUSINESS_STRUCTURE,
  EMPLOYMENT_BASIS,
  EMPLOYMENT_EMPLOYMENT_TYPE,
  EMPLOYMENT_TYPE,
  FIELD_PATHS,
  FIELD_VALUES,
  INCOME_TYPE,
} from '@funnels/constants';
import { AnyFormValues, Bas, BusinessIncomeDetails, Company, CompanyOwner, Income } from '@funnels/types';

import getIndexFromId from './getIndexFromId';

export function getEmploymentCompletedStatus(employmentIndex: number = 0, formValues: AnyFormValues) {
  const validatedValues = [];
  const applicantId = get(formValues, FIELD_PATHS.EMPLOYMENT_APPLICANT_ID(employmentIndex));
  const applicants = get(formValues, FIELD_PATHS.APPLICANTS(), []);
  const applicantIndex = getIndexFromId(applicants, applicantId);
  const isBorrower =
    get(formValues, FIELD_PATHS.APPLICANT_TYPE(applicantIndex), FIELD_VALUES.BORROWER) === FIELD_VALUES.BORROWER;
  const employmentClientId = get(formValues, FIELD_PATHS.EMPLOYMENT_CLIENT_ID(employmentIndex));
  const employmentType = get(formValues, FIELD_PATHS.EMPLOYMENT_TYPE(employmentIndex));
  const employmentStartDate = get(formValues, FIELD_PATHS.EMPLOYMENT_START_DATE(employmentIndex));
  const employmentEndDate = get(formValues, FIELD_PATHS.EMPLOYMENT_END_DATE(employmentIndex));
  const employmentEmploymentType = get(formValues, FIELD_PATHS.EMPLOYMENT_EMPLOYMENT_TYPE(employmentIndex));
  const employmentBasis = get(formValues, FIELD_PATHS.EMPLOYMENT_EMPLOYMENT_BASIS(employmentIndex));
  const employmentMaternityLeaveReturn = get(
    formValues,
    FIELD_PATHS.EMPLOYMENT_MATERNITY_LEAVE_RETURN_BASIS(employmentIndex)
  );
  const employmentRole = get(formValues, FIELD_PATHS.EMPLOYMENT_ROLE_ANZSCO_CODE(employmentIndex));

  const companyClientId = get(formValues, FIELD_PATHS.EMPLOYMENT_COMPANY_CLIENT_ID(employmentIndex));
  const companyIndex = get(formValues, FIELD_PATHS.COMPANIES(), []).findIndex(
    (company: Company) => get({ companies: [company] }, FIELD_PATHS.COMPANY_CLIENT_ID(0)) === companyClientId
  );
  const companyBusinessName = get(formValues, FIELD_PATHS.COMPANY_BUSINESS_NAME(companyIndex));
  const companyBusinessStructure = get(formValues, FIELD_PATHS.COMPANY_BUSINESS_STRUCTURE(companyIndex));
  const companyTrustType = get(formValues, FIELD_PATHS.COMPANY_TRUST_TYPE(companyIndex));
  const companyOwners = get(formValues, FIELD_PATHS.COMPANY_OWNERS(companyIndex), []);
  const companyABN = get(formValues, FIELD_PATHS.COMPANY_ABN(companyIndex));
  const companyPhone = get(formValues, FIELD_PATHS.COMPANY_PHONE_NUMBER(companyIndex));
  const companyAddress = get(formValues, FIELD_PATHS.COMPANY_ADDRESS_FORMATTED(companyIndex));
  const isForeignCompany = get(formValues, FIELD_PATHS.COMPANY_FOREIGN_COMPANY(companyIndex));

  const incomeIndex = get(formValues, FIELD_PATHS.INCOMES(), []).findIndex(
    (income: Income) =>
      get({ incomes: [income] }, FIELD_PATHS.INCOME_COMPANY_CLIENT_ID(0)) === companyClientId ||
      get({ incomes: [income] }, FIELD_PATHS.INCOME_EMPLOYMENT_CLIENT_ID(0)) === employmentClientId
  );

  const incomeType = get(formValues, FIELD_PATHS.INCOME_TYPE(incomeIndex));
  const incomeAmount = get(formValues, FIELD_PATHS.INCOME_AMOUNT(incomeIndex));
  const incomeFullDocIncomes = get(formValues, FIELD_PATHS.INCOME_BUSINESS_INCOMES(incomeIndex), []);
  const incomeLowDocGross = get(formValues, FIELD_PATHS.INCOME_BUSINESS_GROSS(incomeIndex));
  const incomeLowDocBases = get(formValues, FIELD_PATHS.INCOME_BUSINESS_BASES(incomeIndex), []);

  const isNotWorking = employmentEmploymentType === EMPLOYMENT_EMPLOYMENT_TYPE.NOT_WORKING;
  const isPrevious = employmentType === EMPLOYMENT_TYPE.PREVIOUS;
  const isMaternityLeave = employmentBasis === EMPLOYMENT_BASIS.MATERNITY_LEAVE;
  const isAbnVerified = isForeignCompany || !!companyABN;

  validatedValues.push(!!employmentType);
  validatedValues.push(!!employmentEmploymentType);
  validatedValues.push(!!employmentStartDate);

  if (isPrevious) {
    validatedValues.push(!!employmentEndDate);
  }

  // return basic checking if is not employee or self employed
  if (isNotWorking) {
    return validatedValues.every((validatedValue) => validatedValue);
  }

  if (isMaternityLeave) {
    validatedValues.push(!!employmentMaternityLeaveReturn);
  }

  validatedValues.push(!!companyAddress);
  validatedValues.push(!!companyPhone);
  validatedValues.push(!!employmentRole);
  validatedValues.push(!!employmentBasis);
  validatedValues.push(!!companyBusinessName);
  validatedValues.push(!!isAbnVerified);

  // return employee completed status
  if (employmentEmploymentType === EMPLOYMENT_EMPLOYMENT_TYPE.EMPLOYEE) {
    if (!isPrevious && isBorrower) {
      validatedValues.push(!!incomeType);
      validatedValues.push(!!incomeAmount);
    }

    return validatedValues.every((validatedValue) => validatedValue);
  }

  // return self employed completed status
  if (employmentEmploymentType === EMPLOYMENT_EMPLOYMENT_TYPE.SELF_EMPLOYED) {
    const percentOwned = companyOwners.find(
      (_: CompanyOwner, index: number) =>
        get(formValues, FIELD_PATHS.COMPANY_OWNER_APPLICANT_ID(companyIndex, index)) === applicantId &&
        get(formValues, FIELD_PATHS.COMPANY_OWNER_PERCENT_OWNED(companyIndex, index)) > 0
    );

    if (companyBusinessStructure === BUSINESS_STRUCTURE.TRUST) {
      validatedValues.push(!!companyTrustType);
    }

    validatedValues.push(!!companyBusinessStructure);

    if (!isPrevious) {
      validatedValues.push(!!percentOwned);
      if (isBorrower) {
        validatedValues.push(!!incomeType);

        // SE full doc
        if (INCOME_TYPE.SELF_EMPLOYED_BUSINESS_INCOME_FULL_DOC === incomeType) {
          const hasAnyProfitBeforeTax = incomeFullDocIncomes.some((companyIncome: BusinessIncomeDetails) =>
            isFinite(
              get(
                { incomes: [{ incomes: [companyIncome] }] },
                FIELD_PATHS.INCOME_BUSINESS_INCOME_COMPANY_PROFIT_BEFORE_TAX(0, 0)
              )
            )
          );
          validatedValues.push(hasAnyProfitBeforeTax);

          return validatedValues.every((validatedValue) => validatedValue);
        }

        // SE low doc
        if (INCOME_TYPE.SELF_EMPLOYED_BUSINESS_INCOME_LOW_DOC === incomeType) {
          const hasAboveOneBases =
            incomeLowDocBases.filter(
              (bas: Bas) =>
                get({ incomes: [{ bas: [bas] }] }, FIELD_PATHS.INCOME_BUSINESS_BAS_SALES_AMOUNT(0, 0), 0) > 0
            ).length > 1;

          validatedValues.push(hasAboveOneBases);
          validatedValues.push(!!incomeLowDocGross);

          return validatedValues.every((validatedValue) => validatedValue);
        }
      }
    }
    return validatedValues.every((validatedValue) => validatedValue);
  }

  return false;
}
